import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { BookOpen, ArrowUpRight, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const { t, lang } = useLang();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/blog/articles`)
      .then((res) => {
        if (mounted) setArticles(res.data || []);
      })
      .catch((err) => {
        if (mounted) setError(err.message);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const pickTitle = (a) => (lang === "ar" ? a.title_ar || a.title_en : a.title_en || a.title_ar);
  const pickExcerpt = (a) => (lang === "ar" ? a.excerpt_ar || a.excerpt_en : a.excerpt_en || a.excerpt_ar);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <div data-testid="blog-page">
      <section className="relative pt-28 pb-10 md:pt-32 md:pb-14 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-40 start-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full blur-[160px] opacity-25 bg-[#E6A95B]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(10,10,12,0.7)_60%,_#0A0A0C_100%)]" />
        </div>
        <div className="relative container-x max-w-5xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E6A95B]/40 bg-black/40 backdrop-blur text-[#E6A95B] text-xs uppercase tracking-[0.28em] mb-6">
            <BookOpen className="w-4 h-4" /> {t("blog.eyebrow")}
          </div>
          <h1 className="display-h1">{t("blog.title")}</h1>
          <p className="mt-8 text-white/70 text-xl leading-relaxed max-w-3xl">{t("blog.lead")}</p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="container-x">
          {loading && <p className="text-white/50">{t("blog.loading")}</p>}
          {error && <p className="text-red-400">{error}</p>}
          {!loading && articles.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-[#0d0d10] p-12 text-center">
              <BookOpen className="w-10 h-10 text-[#E6A95B] mx-auto mb-6" />
              <h3 className="display-h3 mb-3">{t("blog.emptyTitle")}</h3>
              <p className="text-white/60 max-w-xl mx-auto">{t("blog.emptySub")}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                data-testid={`blog-card-${a.slug}`}
              >
                <a
                  href={`/blog/${a.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="relative aspect-[16/10] overflow-hidden rounded-xl border border-white/10 bg-[#0d0d10]">
                    {a.cover_image ? (
                      <img
                        src={a.cover_image}
                        alt={pickTitle(a)}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-16 h-16 text-[#E6A95B]/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/70 to-transparent" />
                  </div>
                  <div className="mt-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] text-[#E6A95B]">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{formatDate(a.created_at)}</span>
                  </div>
                  <h3 className="mt-3 font-display text-2xl leading-tight group-hover:text-[#E6A95B] transition-colors">
                    {pickTitle(a)}
                  </h3>
                  <p className="mt-2 text-white/60 line-clamp-3">{pickExcerpt(a)}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-white/80 text-sm">
                    <span>{t("blog.readMore")}</span>
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 -translate-y-0.5 transition" />
                  </div>
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
